import { resetAppOffsets } from '@src/composables/useAppSettings';

import { useActiveStudyStore } from './active-study';
import { useAnalysisStore } from './analysis';
import { useFunctionCalculatorStore } from './calculators/FunctionCalculatorStore';
import { useOCGCalculatorStore } from './calculators/OCGCalculatorStore';
import { useContourContainerStore } from './contours/contour-container-store';
import { useContourEventStore } from './contours/contour-event-store';
import { useViewportStateStore } from './contours/viewport-state-store';
import { useCurrentStudyStore } from './dicom-web/current-study-store';
import { seriesClassification } from './dicom-web/SeriesClassification';
import { useImageClipboardStore } from './image-clipboard-store';
import { useAppState } from './ocg-app-state';
import { useReportStore } from './report';
import { useUpdatedReportStore } from './report-store';
import useViewCameraStore from './view-configs/camera';
import useViewSliceStore from './view-configs/slicing';
import useWindowingStore from './view-configs/windowing';

function clearReactiveRecord(obj: Record<string, unknown>) {
  Object.keys(obj).forEach((key) => {
    delete obj[key];
  });
}

export function resetAppState() {
  useContourContainerStore().$reset();
  useViewportStateStore().$reset();
  resetAppOffsets();
  useAnalysisStore().resetStudy();
  useActiveStudyStore().$reset();
  useFunctionCalculatorStore().$reset();
  useOCGCalculatorStore().$reset();

  clearReactiveRecord(useViewSliceStore().configs);
  clearReactiveRecord(useWindowingStore().configs);
  clearReactiveRecord(useViewCameraStore().configs);

  useReportStore().$reset();
  useUpdatedReportStore().resetStore();
  useImageClipboardStore().$reset();
}

/**
 * Resets the currently loaded study to its initial state as if no analysis state
 * was loaded, clearing contours, calculations, annotations, and custom view configs,
 * while keeping the DICOM case and active patient loaded.
 */
export function resetStudyState() {
  const currentStudyStore = useCurrentStudyStore();
  const activeStudyStore = useActiveStudyStore();

  if (!currentStudyStore.getStudyID) {
    return;
  }

  // 1. Reset contours and notify all viewers and thumbnails
  useContourContainerStore().$reset();
  useContourEventStore().emitChange('deleteAll', []);

  // 2. Reset viewport state store
  useViewportStateStore().$reset();

  // 3. Reset heuristic classifications and re-apply default viewport mapping
  seriesClassification.init();
  seriesClassification.applyDefaultViewportMapping();

  // 4. Reset transmurality offsets to default values (10% EPI, 15% ENDO)
  resetAppOffsets();

  // 5. Reset biomarker analysis results
  useAnalysisStore().resetStudy();

  // 6. Reset function & OCG calculator stores
  useFunctionCalculatorStore().$reset();
  useOCGCalculatorStore().$reset();

  // 7. Reset view slicing, windowing, and camera configs to defaults
  clearReactiveRecord(useViewSliceStore().configs);
  clearReactiveRecord(useWindowingStore().configs);
  clearReactiveRecord(useViewCameraStore().configs);

  // 8. Reset report stores
  useReportStore().$reset();
  useUpdatedReportStore().resetStore();

  // 9. Reset image clipboard
  useImageClipboardStore().$reset();

  // 10. Clear manual patient measurements
  currentStudyStore.manualPatientMeasurements = null;

  // 11. Re-initialize active study data (slices/phases) while keeping activePatient & studyKey intact
  activeStudyStore.initStudyData();
  const numSeries = currentStudyStore.getSerieCount();
  for (let seriesIndex = 0; seriesIndex < numSeries; seriesIndex++) {
    const sliceCount = currentStudyStore.getSliceCount(seriesIndex);
    for (let sliceIndex = 0; sliceIndex < sliceCount; sliceIndex++) {
      const numImages = currentStudyStore.getImagesCount(
        seriesIndex,
        sliceIndex,
      );
      activeStudyStore.addStudyData(
        seriesIndex.toString(),
        seriesIndex,
        sliceIndex,
        numImages,
      );
    }
  }
  activeStudyStore.hasSavedState = true;

  // 12. Switch view back to QualityCheck
  void useAppState().setView('QualityCheck');
}
